import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useProgress } from '../context/ProgressContext'
import Quiz from '../components/Quiz'

/* ─── Section navigation labels ─── */
const SECTIONS = [
  'The Big Picture',
  'What\'s in Each Sheet?',
  'Why This Matters',
  'Knowledge Check',
]

/* ─── Quiz data ─── */
const QUIZ = [
  {
    question: 'Where do our click numbers come from?',
    options: [
      'Client reports',
      'HasOffers via BigQuery',
      'We count them manually',
      'Google Analytics',
    ],
    correctIndex: 1,
    explanation:
      'Clicks are tracked by HasOffers when users click on our website, then flow to BigQuery and into our Clicks Sheet.',
  },
  {
    question: 'How many different paths can conversion data take to reach the Conversions Sheet?',
    options: [
      'One — they all come from postbacks',
      'Two — automated and manual',
      'Four — A1 (postback+script), A2 (email+script), M1 (email+manual), M2 (portal+manual)',
      'Three — postbacks, emails, and portals',
    ],
    correctIndex: 2,
    explanation:
      'There are four distinct paths: A1 (postback → BigQuery → script), A2 (client email → script extracts), M1 (client email → manual extract), and M2 (partner portal → manual download). All end up in the same Conversions Sheet.',
  },
  {
    question: 'Why does each offer have its own tab in the Conversions Sheet?',
    options: [
      'It looks prettier',
      'Google Sheets requires it',
      'Because conversion data arrives separately per offer, often from different sources',
      'It\'s faster to load',
    ],
    correctIndex: 2,
    explanation:
      'Since conversions come from different sources (postbacks vs client reports) and arrive at different times, organizing by offer keeps the data clean and traceable.',
  },
  {
    question: 'What is a postback?',
    options: [
      'An email from the client',
      'An automated signal sent when a conversion happens',
      'A backup of the spreadsheet',
      'A type of Google Sheets formula',
    ],
    correctIndex: 1,
    explanation:
      'A postback is an automated HTTP request that fires when a conversion (sale, signup, etc.) occurs. It sends the conversion data directly to our tracking system in real time.',
  },
  {
    question: 'What is the end goal of combining this data?',
    options: [
      'To make one giant spreadsheet with everything',
      'To create a separate report per offer showing clicks, conversions, and conversion rate',
      'To send all the data to the client in one email',
      'To delete the original data',
    ],
    correctIndex: 1,
    explanation:
      'We want a clean, per-offer report that shows clicks alongside conversions and the resulting conversion rate — ready to share with the client.',
  },
  {
    question: 'Which conversion path involves logging into a partner\'s reporting dashboard?',
    options: [
      'A1 — Postback to BigQuery',
      'A2 — Client Email + Script',
      'M1 — Client Email, Manual Extract',
      'M2 — Partner Portal, Manual Download',
    ],
    correctIndex: 3,
    explanation:
      'Path M2 involves manually logging into the partner\'s portal, downloading their conversion report, and entering the data into the sheet.',
  },
  {
    question: 'What is the difference between Path A1 and Path A2?',
    options: [
      'A1 is manual, A2 is automated',
      'A1 uses postbacks to BigQuery, A2 uses client email reports processed by a script',
      'A1 is for clicks, A2 is for conversions',
      'There is no difference — they\'re the same path',
    ],
    correctIndex: 1,
    explanation:
      'Both are automated, but the data source differs. A1 receives real-time postbacks that flow through BigQuery. A2 processes conversion reports emailed by the client using an automated script.',
  },
  {
    question: 'How is the Clicks Sheet structured compared to the Conversions Sheet?',
    options: [
      'Both have one tab per offer',
      'Clicks has one tab per offer, Conversions has all offers in one tab',
      'Clicks has all offers in one tab, Conversions has one tab per offer',
      'Both have all offers in one tab',
    ],
    correctIndex: 2,
    explanation:
      'The Clicks Sheet contains all offers in a single sheet (with an Offer ID column), while the Conversions Sheet has a separate tab for each offer because conversion data arrives separately per offer.',
  },
  {
    question: 'Why might different offers have different conversion column names (e.g., "Sign-ups" vs "Funded Loans")?',
    options: [
      'Someone made a typo when setting up the sheet',
      'Each offer SEED_COURSES a different action as its conversion event',
      'Google Sheets randomly assigns column names',
      'The column names come from BigQuery and can\'t be changed',
    ],
    correctIndex: 1,
    explanation:
      'Different offers count different actions as conversions. One offer might track sign-ups, another SEED_COURSES app approvals, and another SEED_COURSES funded loans. The column name reflects the specific conversion event for that offer.',
  },
  {
    question: 'What role does BigQuery play in the data pipeline?',
    options: [
      'It\'s where we write our final reports',
      'It\'s our data warehouse that stores click and postback data before it reaches the sheets',
      'It\'s a Google Sheets plugin',
      'It only stores conversion data, not click data',
    ],
    correctIndex: 1,
    explanation:
      'BigQuery is our data warehouse. Both click data (from HasOffers) and postback conversion data flow into BigQuery first, and then scripts pull from BigQuery into our Google Sheets.',
  },
  {
    question: 'Which paths are considered "manual" in the conversion data flow?',
    options: [
      'A1 and A2',
      'A1 and M1',
      'M1 and M2',
      'A2 and M2',
    ],
    correctIndex: 2,
    explanation:
      'M1 (client email with manual extraction) and M2 (partner portal with manual download) are the two manual paths. The "M" stands for manual — both require a person to extract and enter the data.',
  },
  {
    question: 'A new offer is set up with real-time postbacks. Which conversion path will its data follow?',
    options: [
      'M1 — Client Email, Manual Extract',
      'A1 — Postback to BigQuery to script',
      'M2 — Partner Portal, Manual Download',
      'A2 — Client Email + Script',
    ],
    correctIndex: 1,
    explanation:
      'If an offer has real-time postbacks configured, the data follows Path A1: postback fires → HasOffers records it → data flows to BigQuery → a script pulls it into the Conversions Sheet automatically.',
  },
]

/* ─── Reusable animated box for the data flow ─── */
function FlowBox({ children, color, delay = 0, className = '' }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-400 text-blue-800',
    green: 'bg-green-50 border-green-400 text-green-800',
    amber: 'bg-amber-50 border-amber-400 text-amber-800',
    white: 'bg-white border-gray-300 text-gray-800',
    'white-blue': 'bg-white border-blue-400 text-gray-800',
    'white-green': 'bg-white border-green-400 text-gray-800',
    'white-amber': 'bg-white border-amber-400 text-gray-800',
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className={`rounded-xl border-2 px-5 py-3 font-medium shadow-sm ${colorMap[color] || colorMap.white} ${className}`}
    >
      {children}
    </motion.div>
  )
}

/* ─── Animated arrow ─── */
function Arrow({ delay = 0, direction = 'down', label = '' }) {
  const isDown = direction === 'down'
  const isRight = direction === 'right'

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay, duration: 0.4 }}
      className={`flex items-center justify-center ${isDown ? 'flex-col py-1' : 'flex-row px-1'}`}
    >
      {label && (
        <span className="mb-1 text-xs font-medium text-gray-500">{label}</span>
      )}
      <svg
        className={`text-gray-400 ${isDown ? 'h-8 w-6' : 'h-6 w-8'}`}
        fill="none"
        viewBox={isDown ? '0 0 24 32' : '0 0 32 24'}
        stroke="currentColor"
        strokeWidth={2}
      >
        {isDown && (
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v24m0 0l-6-6m6 6l6-6" />
        )}
        {isRight && (
          <path strokeLinecap="round" strokeLinejoin="round" d="M2 12h24m0 0l-6-6m6 6l-6 6" />
        )}
      </svg>
    </motion.div>
  )
}

/* ─── Mini spreadsheet component ─── */
function MiniSheet({ headers, rows, headerColor = 'bg-gray-100' }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className={headerColor}>
            {headers.map((h) => (
              <th key={h} className="px-4 py-2.5 text-left font-semibold text-gray-700">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2 text-gray-600">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Section 1: The Big Picture ─── */
function SectionBigPicture() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">The Big Picture</h2>
        <p className="text-lg text-gray-600">
          Before you touch a single formula, let's understand where the numbers actually come from.
          There are two separate data flows that feed into your spreadsheets.
        </p>
      </div>

      {/* ── Clicks Path ── */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-blue-700">
          Path 1: How Clicks Get to Your Sheet
        </h3>
        <div className="flex flex-col items-center">
          <FlowBox color="blue" delay={0}>
            <div className="text-center">
              <div className="text-lg">👤 User clicks on the website</div>
              <div className="text-xs opacity-70">A visitor clicks one of our links</div>
            </div>
          </FlowBox>
          <Arrow delay={0.3} />
          <FlowBox color="blue" delay={0.5}>
            <div className="text-center">
              <div className="text-lg">📡 HasOffers records the click</div>
              <div className="text-xs opacity-70">Our tracking platform logs every click</div>
            </div>
          </FlowBox>
          <Arrow delay={0.7} />
          <FlowBox color="blue" delay={0.9}>
            <div className="text-center">
              <div className="text-lg">🗄️ Data flows to BigQuery</div>
              <div className="text-xs opacity-70">Our data warehouse stores everything</div>
            </div>
          </FlowBox>
          <Arrow delay={1.1} />
          <FlowBox color="white-blue" delay={1.3}>
            <div className="text-center">
              <div className="text-lg">📊 Clicks Sheet</div>
              <div className="text-xs opacity-70">Aggregated daily clicks per offer in Google Sheets</div>
            </div>
          </FlowBox>
        </div>
      </div>

      {/* ── Conversions Paths ── */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-700">
          Path 2: How Conversions Get to Your Sheet
        </h3>
        <p className="mb-6 text-gray-600">
          Conversions (sales, signups, etc.) arrive from <strong>four different paths</strong> depending on the offer.
          Two are automated, two are manual.
        </p>

        {/* Automated paths */}
        <div className="mb-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-4 flex items-center gap-2"
          >
            <div className="h-px flex-1 bg-green-200" />
            <span className="rounded-full bg-green-100 px-4 py-1 text-sm font-semibold text-green-700">
              Automated Paths
            </span>
            <div className="h-px flex-1 bg-green-200" />
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Path A1: Postbacks */}
            <div className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mb-3 rounded-full bg-green-100 px-4 py-1 text-xs font-semibold text-green-700"
              >
                Path A1 — Postback to BigQuery
              </motion.div>
              <FlowBox color="green" delay={0.3}>
                <div className="text-center">
                  <div className="text-lg">⚡ Postback fires</div>
                  <div className="text-xs opacity-70">Automated signal when a conversion happens</div>
                </div>
              </FlowBox>
              <Arrow delay={0.5} />
              <FlowBox color="green" delay={0.6}>
                <div className="text-center">
                  <div className="text-lg">📡 HasOffers → BigQuery</div>
                  <div className="text-xs opacity-70">Real-time, reliable, stored automatically</div>
                </div>
              </FlowBox>
              <Arrow delay={0.8} />
              <FlowBox color="green" delay={0.9}>
                <div className="text-center">
                  <div className="text-lg">⚙️ Script pulls data</div>
                  <div className="text-xs opacity-70">Automated script queries BigQuery</div>
                </div>
              </FlowBox>
              <Arrow delay={1.1} />
              <FlowBox color="white-green" delay={1.2}>
                <div className="text-center">
                  <div className="text-lg">📋 Conversions Sheet</div>
                  <div className="text-xs opacity-70">1 offer per tab, populated automatically</div>
                </div>
              </FlowBox>
            </div>

            {/* Path A2: Client email → script extracts */}
            <div className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="mb-3 rounded-full bg-green-100 px-4 py-1 text-xs font-semibold text-green-700"
              >
                Path A2 — Client Email + Script
              </motion.div>
              <FlowBox color="green" delay={0.4}>
                <div className="text-center">
                  <div className="text-lg">📧 Client emails a report</div>
                  <div className="text-xs opacity-70">CSV or Excel file with their data</div>
                </div>
              </FlowBox>
              <Arrow delay={0.6} />
              <FlowBox color="green" delay={0.7}>
                <div className="text-center">
                  <div className="text-lg">⚙️ Script extracts data</div>
                  <div className="text-xs opacity-70">Automated extraction from the report</div>
                </div>
              </FlowBox>
              <Arrow delay={0.9} />
              <FlowBox color="green" delay={1.0}>
                <div className="text-center">
                  <div className="text-lg">📥 Inserts into sheet</div>
                  <div className="text-xs opacity-70">Script writes to the correct offer tab</div>
                </div>
              </FlowBox>
              <Arrow delay={1.2} />
              <FlowBox color="white-green" delay={1.3}>
                <div className="text-center">
                  <div className="text-lg">📋 Conversions Sheet</div>
                  <div className="text-xs opacity-70">1 offer per tab, populated by script</div>
                </div>
              </FlowBox>
            </div>
          </div>
        </div>

        {/* Manual paths */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-4 flex items-center gap-2"
          >
            <div className="h-px flex-1 bg-amber-200" />
            <span className="rounded-full bg-amber-100 px-4 py-1 text-sm font-semibold text-amber-700">
              Manual Paths
            </span>
            <div className="h-px flex-1 bg-amber-200" />
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Path M1: Client email → manual extract */}
            <div className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mb-3 rounded-full bg-amber-100 px-4 py-1 text-xs font-semibold text-amber-700"
              >
                Path M1 — Client Email, Manual Extract
              </motion.div>
              <FlowBox color="amber" delay={0.3}>
                <div className="text-center">
                  <div className="text-lg">📧 Client emails a report</div>
                  <div className="text-xs opacity-70">CSV or Excel file with their data</div>
                </div>
              </FlowBox>
              <Arrow delay={0.5} />
              <FlowBox color="amber" delay={0.6}>
                <div className="text-center">
                  <div className="text-lg">👁️ We review & extract</div>
                  <div className="text-xs opacity-70">Manual process, can be delayed</div>
                </div>
              </FlowBox>
              <Arrow delay={0.8} />
              <FlowBox color="amber" delay={0.9}>
                <div className="text-center">
                  <div className="text-lg">📝 Data entered into sheet</div>
                  <div className="text-xs opacity-70">May need reformatting</div>
                </div>
              </FlowBox>
              <Arrow delay={1.1} />
              <FlowBox color="white-amber" delay={1.2}>
                <div className="text-center">
                  <div className="text-lg">📋 Conversions Sheet</div>
                  <div className="text-xs opacity-70">1 offer per tab, entered manually</div>
                </div>
              </FlowBox>
            </div>

            {/* Path M2: Partner portal → manual download */}
            <div className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="mb-3 rounded-full bg-amber-100 px-4 py-1 text-xs font-semibold text-amber-700"
              >
                Path M2 — Partner Portal, Manual Download
              </motion.div>
              <FlowBox color="amber" delay={0.4}>
                <div className="text-center">
                  <div className="text-lg">🌐 Log into partner portal</div>
                  <div className="text-xs opacity-70">Access the partner's reporting dashboard</div>
                </div>
              </FlowBox>
              <Arrow delay={0.6} />
              <FlowBox color="amber" delay={0.7}>
                <div className="text-center">
                  <div className="text-lg">📥 Download the report</div>
                  <div className="text-xs opacity-70">Export conversion data as CSV/Excel</div>
                </div>
              </FlowBox>
              <Arrow delay={0.9} />
              <FlowBox color="amber" delay={1.0}>
                <div className="text-center">
                  <div className="text-lg">📝 Data entered into sheet</div>
                  <div className="text-xs opacity-70">May need reformatting</div>
                </div>
              </FlowBox>
              <Arrow delay={1.2} />
              <FlowBox color="white-amber" delay={1.3}>
                <div className="text-center">
                  <div className="text-lg">📋 Conversions Sheet</div>
                  <div className="text-xs opacity-70">1 offer per tab, entered manually</div>
                </div>
              </FlowBox>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 italic mt-2">
        Note: These pathways represent the bulk of our cases. There are some edge case pathways not illustrated here — this is not an exhaustive list.
      </p>

      {/* Summary callout */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="rounded-xl border border-gray-200 bg-gray-50 p-6"
      >
        <p className="text-center text-gray-700">
          <strong>Key takeaway:</strong> Clicks always flow through the same automated pipeline.
          Conversions arrive via four different paths — <span className="font-semibold text-green-700">A1</span> (postback + script),
          <span className="font-semibold text-green-700"> A2</span> (client email + script),
          <span className="font-semibold text-amber-700"> M1</span> (client email + manual),
          or <span className="font-semibold text-amber-700"> M2</span> (partner portal + manual)
          — but they all end up in the same Conversions Sheet, one offer per tab.
        </p>
      </motion.div>
    </div>
  )
}

/* ─── Section 2: What's in Each Sheet? ─── */
function SectionSheetContents() {
  const [activeTab, setActiveTab] = useState('offer_123')

  const conversionsTabs = {
    offer_123: {
      headers: ['Date', 'Sign-ups'],
      rows: [
        ['2024-01-15', '45'],
        ['2024-01-16', '38'],
        ['2024-01-17', '52'],
      ],
      color: 'bg-green-50',
      note: 'This offer SEED_COURSES sign-ups as the conversion event.',
    },
    offer_456: {
      headers: ['Date', 'Product X App Approvals'],
      rows: [
        ['2024-01-15', '12'],
        ['2024-01-16', '15'],
        ['2024-01-17', '9'],
      ],
      color: 'bg-amber-50',
      note: 'This offer SEED_COURSES app approvals — the column heading comes from the client\'s report.',
    },
    offer_789: {
      headers: ['Date', 'Funded Loans'],
      rows: [
        ['2024-01-15', '3'],
        ['2024-01-16', '5'],
        ['2024-01-17', '2'],
      ],
      color: 'bg-purple-50',
      note: 'This offer counts funded loans as conversions. Different offer, different metric — but same structure.',
    },
  }

  const activeData = conversionsTabs[activeTab]

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">What's in Each Sheet?</h2>
        <p className="text-lg text-gray-600">
          Let's look at the actual data you'll be working with. These are hypothetical examples
          of what each sheet contains.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Clicks Sheet */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-blue-400" />
            <h3 className="text-lg font-semibold text-gray-800">Clicks Sheet</h3>
          </div>
          <p className="mb-3 text-sm text-gray-500">
            One sheet, all offers. Daily click totals pulled from BigQuery.
          </p>
          <MiniSheet
            headerColor="bg-blue-50"
            headers={['Date', 'Offer ID', 'Clicks']}
            rows={[
              ['2024-01-15', 'offer_123', '1,250'],
              ['2024-01-15', 'offer_456', '890'],
              ['2024-01-15', 'offer_789', '2,100'],
              ['2024-01-16', 'offer_123', '1,100'],
              ['2024-01-16', 'offer_456', '950'],
              ['2024-01-16', 'offer_789', '1,870'],
            ]}
          />
        </motion.div>

        {/* Conversions Sheet */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-purple-400" />
            <h3 className="text-lg font-semibold text-gray-800">Conversions Sheet</h3>
          </div>
          <p className="mb-3 text-sm text-gray-500">
            One tab per offer. Click the tabs to see how each offer's data looks different.
          </p>

          {/* Tab bar */}
          <div className="mb-0 flex gap-1">
            {Object.keys(conversionsTabs).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-t-lg border border-b-0 px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'border-gray-200 bg-white text-gray-900 shadow-sm'
                    : 'border-transparent bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <MiniSheet
                headerColor={activeData.color}
                headers={activeData.headers}
                rows={activeData.rows}
              />
            </motion.div>
          </AnimatePresence>

          <motion.p
            key={activeTab + '-note'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500"
          >
            💡 {activeData.note}
          </motion.p>

          <p className="mt-2 text-xs text-gray-400">
            Notice how each offer's tab has a different column heading for conversions.
            The column name reflects what that specific offer counts as a "conversion" — sign-ups, approvals, funded loans, etc.
          </p>
        </motion.div>
      </div>
    </div>
  )
}

/* ─── Section 3: Why This Matters ─── */
function SectionWhyItMatters() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Why This Matters</h2>
        <p className="text-lg text-gray-600">
          Understanding the data pipeline is essential before you start building reports.
          Here's what you need to know.
        </p>
      </div>

      {/* Key points */}
      <div className="space-y-4">
        {[
          {
            icon: '🎯',
            text: 'Your job is to combine these two data sources into a single report for each offer.',
          },
          {
            icon: '🔄',
            text: 'The clicks always come from the same place, but conversions vary by offer.',
          },
          {
            icon: '📚',
            text: 'In the following modules, you\'ll learn exactly how to do this.',
          },
        ].map((point, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15, duration: 0.4 }}
            className="flex items-start gap-4 rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
          >
            <span className="text-2xl">{point.icon}</span>
            <p className="text-gray-700">{point.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

/* ─── Section 4: Knowledge Check — uses shared Quiz component ─── */

/* ─── Main Module Component ─── */
export default function Module0() {
  const [activeSection, setActiveSection] = useState(0)
  const navigate = useNavigate()
  const { completeModule } = useProgress()
  const sectionRefs = useRef([])

  const scrollToSection = (index) => {
    setActiveSection(index)
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Quiz handles completion internally via the shared component

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <button
            onClick={() => navigate('/courses/sheets')}
            className="mb-4 flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to modules
          </button>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
              Module 0
            </span>
            <span className="text-sm text-gray-400">The Big Picture</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">Where Does Our Data Come From?</h1>
          <p className="mt-1 text-gray-500">
            Understanding the data pipeline before touching a formula
          </p>
        </div>
      </div>

      {/* Section navigation */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {SECTIONS.map((label, i) => (
              <button
                key={i}
                onClick={() => scrollToSection(i)}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeSection === i
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{
                  backgroundColor: activeSection >= i ? '#3b82f6' : '#d1d5db',
                }} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="mx-auto max-w-4xl px-6 py-10 space-y-16">
        <motion.div
          ref={(el) => (sectionRefs.current[0] = el)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="scroll-mt-20 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
          onViewportEnter={() => setActiveSection(0)}
        >
          <SectionBigPicture />
        </motion.div>

        <motion.div
          ref={(el) => (sectionRefs.current[1] = el)}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
          className="scroll-mt-20 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
          onViewportEnter={() => setActiveSection(1)}
        >
          <SectionSheetContents />
        </motion.div>

        <motion.div
          ref={(el) => (sectionRefs.current[2] = el)}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
          className="scroll-mt-20 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
          onViewportEnter={() => setActiveSection(2)}
        >
          <SectionWhyItMatters />
        </motion.div>

        <motion.div
          ref={(el) => (sectionRefs.current[3] = el)}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
          className="scroll-mt-20 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
          onViewportEnter={() => setActiveSection(3)}
        >
          <Quiz moduleId={0} questions={QUIZ} />
        </motion.div>
      </div>
    </div>
  )
}
